import { useNavigate } from 'react-router-dom';

interface InsufficientTokensModalProps {
  isOpen: boolean;
  onClose: () => void;
  requiredTokens: number;
  currentTokens: number;
}

export default function InsufficientTokensModal({
  isOpen,
  onClose,
  requiredTokens,
  currentTokens,
}: InsufficientTokensModalProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const neededTokens = requiredTokens - currentTokens;

  const handleCharge = () => {
    onClose();
    navigate('/checkout');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fadeIn">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="relative w-full max-w-md animate-scaleIn">
        {/* Glow Effect */}
        <div className="absolute -inset-2 bg-gradient-to-r from-red-500 via-orange-500 to-red-500 rounded-3xl blur-2xl opacity-40 animate-pulse"></div>
        
        {/* Modal Content */}
        <div className="relative bg-gradient-to-br from-primary-dark/98 via-mystic-dark/95 to-primary-dark/98 backdrop-blur-xl rounded-3xl border-2 border-red-500/50 shadow-2xl overflow-hidden">
          {/* Decorative Elements */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-orange-500 to-transparent"></div>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 flex items-center justify-center text-red-400 hover:text-red-300 transition-all hover:scale-110 cursor-pointer z-10"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          <div className="p-8 sm:p-10">
            {/* Icon */}
            <div className="relative w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-red-500 to-orange-600 rounded-full blur-xl opacity-60 animate-pulse"></div>
              <div className="relative w-full h-full rounded-full bg-gradient-to-br from-red-500 via-orange-500 to-red-600 flex items-center justify-center shadow-2xl shadow-red-500/50">
                <i className="ri-error-warning-fill text-white text-4xl"></i>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl sm:text-3xl font-bold text-center mb-3 bg-gradient-to-r from-red-400 via-orange-400 to-red-400 bg-clip-text text-transparent">
              토큰이 부족합니다
            </h3>

            {/* Description */}
            <p className="text-center text-gray-300 mb-8 text-sm sm:text-base">
              이 서비스를 이용하려면 추가 토큰이 필요합니다
            </p>

            {/* Token Info Cards */}
            <div className="space-y-3 mb-8">
              {/* Required Tokens */}
              <div className="bg-primary-dark/60 rounded-2xl border border-primary-gold/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-gold/20 to-yellow-500/20 flex items-center justify-center">
                    <i className="ri-vip-diamond-fill text-primary-gold text-lg"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">필요한 토큰</span>
                </div>
                <span className="text-xl font-bold text-primary-gold">{requiredTokens}</span>
              </div>

              {/* Current Tokens */}
              <div className="bg-primary-dark/60 rounded-2xl border border-gray-600/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-500/20 to-gray-600/20 flex items-center justify-center">
                    <i className="ri-wallet-3-fill text-gray-400 text-lg"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">현재 보유</span>
                </div>
                <span className="text-xl font-bold text-gray-400">{currentTokens}</span>
              </div>

              {/* Needed Tokens */}
              <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl border border-red-500/40 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/30 to-orange-500/30 flex items-center justify-center">
                    <i className="ri-add-circle-fill text-red-400 text-lg"></i>
                  </div>
                  <span className="text-red-300 text-sm font-medium">추가 필요</span>
                </div>
                <span className="text-xl font-bold text-red-400">+{neededTokens}</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3.5 rounded-full bg-gray-700/50 hover:bg-gray-700/70 border border-gray-600/50 text-gray-300 font-semibold transition-all hover:scale-105 cursor-pointer"
              >
                취소
              </button>
              <button
                onClick={handleCharge}
                className="group relative flex-1 px-6 py-3.5 rounded-full bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-600 text-primary-dark font-bold transition-all hover:scale-105 cursor-pointer overflow-hidden shadow-lg shadow-primary-gold/30"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <i className="ri-vip-diamond-fill text-lg"></i>
                  토큰 충전하기
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
